/**
 * @file Routage type des commandes vers les services (table exhaustive : le compilateur exige un handler par type de commande).
 */
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { SimContext } from '../Context';
import type { Command, CommandOf, CommandType } from './Command';

type Handler<T extends CommandType> = (ctx: SimContext, cmd: CommandOf<T>) => Result<unknown>;
type HandlerTable = { [T in CommandType]: Handler<T> };

const HANDLERS: HandlerTable = {
  'upgrade.buy': (c, m) => c.services.upgrades.buy(m.id, m.count),
  'weapon.draw': (c, m) => c.services.weapons.draw(m.pool, m.count),
  'weapon.dailyFreeDraw': (c) => c.services.weapons.dailyFreeDraw(),
  'weapon.merge': (c, m) => c.services.weapons.merge(m.a, m.b),
  'weapon.autoMerge': (c) => ({ ok: true, value: c.services.weapons.queueAutoMerge() }),
  'weapon.setAutoMerge': (c, m) => {
    c.data.weapons.autoMerge = m.enabled;
    if (m.enabled) c.services.weapons.queueAutoMerge();
    return okVoid;
  },
  'weapon.equip': (c, m) => c.services.weapons.equip(m.uid, m.slot),
  'weapon.unequip': (c, m) => c.services.weapons.unequip(m.slot),
  'weapon.lock': (c, m) => c.services.weapons.setLock(m.uid, m.locked),
  'weapon.buyChestSlot': (c) => c.services.weapons.buyChestSlot(),
  'weapon.claimCollection': (c, m) => c.services.weapons.claimCollection(m.grade),
  'star.upgrade': (c, m) => c.services.stars.upgrade(m.slot),
  'skin.buy': (c, m) => c.services.skins.buy(m.id),
  'skin.equip': (c, m) => c.services.skins.equip(m.id),
  'aura.buy': (c, m) => c.services.auras.buy(m.id),
  'aura.equip': (c, m) => c.services.auras.equip(m.id),
  'aura.toggle': (c, m) => c.services.auras.toggle(m.enabled),
  'shotfx.buy': (c, m) => c.services.shotFx.buy(m.id),
  'shotfx.equip': (c, m) => c.services.shotFx.equip(m.id),
  'firework.buy': (c, m) => c.services.fireworks.buy(m.id),
  'firework.equip': (c, m) => c.services.fireworks.equip(m.id),
  'hub.zoom': (c, m) => c.services.hub.zoom(m.action),
  'hub.stow': (c, m) => c.services.hub.stow(m.stowed),
  'hub.firework': (c) => c.services.hub.firework(),
  'hub.net.online': (c, m) => c.services.hub.setNetOnline(m.online),
  'hub.net.catnipAdd': (c, m) => c.services.hub.netCatnipAdd(m.id, m.x, m.y),
  'hub.net.catnipRemove': (c, m) => c.services.hub.netCatnipRemove(m.id),
  'hub.net.catnipCredit': (c, m) => c.services.hub.netCatnipCredit(m.id, m.gain),
  'ammo.buy': (c, m) => c.services.ammoSkins.buy(m.id),
  'ammo.equip': (c, m) => c.services.ammoSkins.equip(m.id),
  'buff.activate': (c, m) => c.services.buffs.activate(m.id, m.useFree),
  'rebirth.perform': (c, m) => c.services.rebirth.perform(m.useDailyBonus),
  'rebirth.upgrade': (c, m) => c.services.rebirth.upgrade(m.stat, m.count),
  'mode.start': (c, m) => c.modes.start(m.mode, m.params ?? {}),
  'mode.exit': (c) => {
    c.modes.returnHome('quit');
    return okVoid;
  },
  'mode.challenge': (c) => c.modes.handleCommand({ type: 'challenge' }),
  'mode.farm': (c) => c.modes.handleCommand({ type: 'farm' }),
  'offline.claim': (c, m) => c.services.offline.claim(m.doubleWithDia),
  'mission.claim': (c, m) => c.services.missions.claim(m.id),
  'pass.claim': (c, m) => c.services.pass.claim(m.tier, m.track),
  'pass.unlockEpic': (c) => c.services.pass.unlockEpic(),
  'daily.claim': (c) => c.services.daily.claim(),
  'relic.draw': (c, m) => c.services.collectibles.drawRelic(m.biome),
  'pet.equip': (c, m) => c.services.collectibles.equipPet(m.petId, m.slot),
  'pet.buy': (c, m) => c.services.collectibles.buyPet(m.petId),
  'pet.unequip': (c, m) => c.services.collectibles.unequipPet(m.petId),
  'adventure.start': (c, m) => c.services.collectibles.startExpedition(m.biome, m.petId),
  'adventure.claim': (c) => c.services.collectibles.claimExpeditions(),
  'settings.set': (c, m) => {
    Object.assign(c.data.settings, m.patch);
    c.modes.refreshControl();
    return okVoid;
  },
  'tutorial.skip': (c) => {
    c.services.tutorial.skip();
    return okVoid;
  },
  'ui.screenOpened': (c, m) => {
    c.bus.emit('ScreenOpened', { screen: m.screen });
    return okVoid;
  },
  'ui.pause': (c, m) => {
    c.rt.paused = m.paused;
    return okVoid;
  },
  'server.reward': (c, m) => {
    // le serveur ne reclame une recompense qu'une fois ; on borne quand meme chaque quantite (jamais negative, plafonnee)
    const cap = (n: number | undefined, max: number): number => (n !== undefined && Number.isFinite(n) ? Math.max(0, Math.min(max, Math.floor(n))) : 0);
    const grants: Partial<Record<WalletId, number>> = {};
    for (const [id, v] of Object.entries(m.bundle.wallet ?? {}) as Array<[WalletId, number | string | undefined]>) {
      const n = cap(typeof v === 'number' ? v : Number(v), 1_000_000);
      if (n > 0) grants[id] = n;
    }
    const tickets: NonNullable<typeof m.bundle.tickets> = {};
    for (const [id, v] of Object.entries(m.bundle.tickets ?? {}) as Array<[keyof typeof tickets, number | undefined]>) {
      const n = cap(v, 100);
      if (n > 0) tickets[id] = n;
    }
    const draws = (m.bundle.draws ?? []).map((d) => ({ pool: d.pool, count: cap(d.count, 20) })).filter((d) => d.count > 0);
    c.tx.grantBundle({ wallet: grants, tickets, draws }, `server:${m.source}`);
    return okVoid;
  },
  'profile.nickname': (c, m) => {
    c.data.meta.nickname = m.name.trim().slice(0, 24);
    return okVoid;
  },
  'debug.grant': (c, m) => {
    const amounts: Partial<Record<WalletId, Decimal>> = {};
    for (const [k, v] of Object.entries(m.amounts) as Array<[WalletId, number | string]>) amounts[k] = Decimal.fromValue(v);
    c.wallet.grant(amounts, 'debug');
    return okVoid;
  },
  'debug.demolishHouse': (c) => c.services.hub.demolishNearestHouse(),
  'debug.hubSeason': (c, m) => c.services.hub.forceSeason(m.season),
  'debug.setStage': (c, m) => {
    c.data.run.stageValue = Math.max(0, Math.floor(m.stageValue));
    c.data.run.bestStageValue = Math.max(c.data.run.bestStageValue, c.data.run.stageValue);
    c.data.progress.bestStageValue = Math.max(c.data.progress.bestStageValue, c.data.run.stageValue);
    c.modes.start('main', {}, true);
    return okVoid;
  },
};

export const dispatchCommand = (ctx: SimContext, cmd: Command): Result<unknown> => {
  const handler = HANDLERS[cmd.type] as Handler<CommandType>;
  return handler(ctx, cmd as CommandOf<CommandType>);
};
